import classNames from 'classnames'
import PropTypes from 'prop-types'
import { generatePath } from 'react-router-dom'

import Modal from 'components/Core/Modal'
import Table from 'components/Core/Table/Table'
import { NEW_ORDER_REVIEW } from 'routes/paths'

import messages from './ExcelSummaryModal.messages'
import styles from './ExcelSummaryModal.module.css'

const UnsuccessfulImportRow = ({ label, error }) => (
  <Table.Row>
    <Table.Cell>
      <div className={classNames(styles.messageIcon, styles.importError)} />
      {label}
    </Table.Cell>
    <Table.Cell>{error}</Table.Cell>
    <Table.Cell />
  </Table.Row>
)

UnsuccessfulImportRow.propTypes = {
  error: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
}

const SuccessfulImportRow = ({ isBrandAccount, label, order }) => (
  <Table.Row>
    <Table.Cell>
      <div className={classNames(styles.messageIcon, styles.importSuccess)} />
      {label}
    </Table.Cell>
    <Table.Cell>
      {isBrandAccount
        ? messages.newPendingOrderCreated
        : messages.newOrderAddedToCart}
    </Table.Cell>
    <Table.Cell
      link={generatePath(NEW_ORDER_REVIEW, {
        id: order.id,
      })}
      shouldLinkOpenInNewTab
      className={styles.poNumber}
    >
      {order.poNumber}
    </Table.Cell>
  </Table.Row>
)

SuccessfulImportRow.propTypes = {
  isBrandAccount: PropTypes.bool,
  label: PropTypes.string.isRequired,
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    poNumber: PropTypes.string,
  }).isRequired,
}

// sc-247036: one Excel Order file can contain several worksheets (one per
// linesheet/collection). Renders one row per worksheet outcome instead of a
// single row for the whole file, so the brand can see exactly which
// linesheets were imported, skipped, or failed, and why.
const renderSheetResultRows = (filename, sheetResults, isBrandAccount) =>
  sheetResults.flatMap((sheetResult) => {
    const label = `${filename} — ${sheetResult.sheetName}`
    if (sheetResult.status === 'created') {
      return sheetResult.orders.map((order) => (
        <SuccessfulImportRow
          isBrandAccount={isBrandAccount}
          label={label}
          order={order}
          key={order.id}
        />
      ))
    }
    return [
      <UnsuccessfulImportRow
        label={label}
        error={sheetResult.reason}
        key={label}
      />,
    ]
  })

const ExcelSummaryModal = ({
  importedFiles,
  isBrandAccount,
  onClose,
  openExcelOrdersModal,
}) => (
  <Modal
    title={messages.excelOrders}
    onClose={onClose}
    open
    large
    secondaryActionHide
    primaryActionLabel={messages.close}
    secondaryActionClose={false}
    secondaryActionLabel={messages.goBack}
    secondaryActionOnClick={openExcelOrdersModal}
    className={styles.ExcelSummaryModal}
  >
    <Table fullWidth>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{messages.file}</Table.HeaderCell>
          <Table.HeaderCell>{messages.result}</Table.HeaderCell>
          <Table.HeaderCell>{messages.poNumber}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {importedFiles.map(({ error, filename, orders, sheetResults }) => {
          // sc-247036: when the backend reports a per-worksheet breakdown, use it —
          // it's the accurate picture for multi-linesheet Excel Order files.
          if (sheetResults && sheetResults.length > 0) {
            return renderSheetResultRows(filename, sheetResults, isBrandAccount)
          }
          if (error) {
            return (
              <UnsuccessfulImportRow
                label={filename}
                error={error}
                key={filename}
              />
            )
          }
          return orders.map((order) => (
            <SuccessfulImportRow
              isBrandAccount={isBrandAccount}
              label={filename}
              order={order}
              key={order.id}
            />
          ))
        })}
      </Table.Body>
    </Table>
  </Modal>
)

const orderShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  poNumber: PropTypes.string,
})

ExcelSummaryModal.propTypes = {
  importedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      error: PropTypes.string,
      filename: PropTypes.string,
      orders: PropTypes.arrayOf(orderShape),
      sheetResults: PropTypes.arrayOf(
        PropTypes.shape({
          sheetName: PropTypes.string,
          status: PropTypes.oneOf(['created', 'error']),
          reason: PropTypes.string,
          orders: PropTypes.arrayOf(orderShape),
        }),
      ),
    }),
  ),
  isBrandAccount: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  openExcelOrdersModal: PropTypes.func,
}

ExcelSummaryModal.defaultProps = {
  importedFiles: [],
  isBrandAccount: false,
  openExcelOrdersModal: () => {},
}

export default ExcelSummaryModal
